/* @jsx h */
import { defineComponent, ref, h, computed, watch, type VNode } from "vue";
import {
  RightSheet,
  SchemaForm,
  listTemplate,
  useLocalization,
  type FormOptions,
  type StructLocals,
} from "@lemmonhq/uikit";
import { Type } from "@sinclair/typebox";
import type { RestaurantAsset, UploadsAPI } from "@app/data/api/uploads";
import { uploadsAPI } from "@app/data/api/uploads";
import { assetCollectionTypeFromName } from "./collectionType";

export interface AssetTypeOption {
  value: string;
  label: string;
}

const NEW_COLLECTION_VALUE = "__new_collection__";
const MAX_IMAGE_UPLOAD_BYTES = 5 * 1024 * 1024;

export interface AssetDetailsProps {
  mode: "add" | "view";
  asset?: RestaurantAsset | null;
  assetType: string;
  isOpen: boolean;
  onClose: () => void;
  onSave?: (asset: RestaurantAsset) => void;
  /** When set, the upload form shows a collection picker seeded with these. */
  typeOptions?: AssetTypeOption[];
  /** Owner-scoped asset API (Shop by default; the Group manager passes its own). */
  uploadsApi?: UploadsAPI;
}

// Meta keys the server derives from the stored object or owns elsewhere
// (position is set by reorder); everything else is user-authored custom metadata
// editable as key/value pairs.
const RESERVED_META_KEYS = new Set([
  "content_type",
  "original_content_type",
  "width",
  "height",
  "position",
  "tags",
]);

type MetaEntry = { key: string; value: string };

const extractCustomMeta = (
  asset?: RestaurantAsset | null,
): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  const meta = (asset?.meta ?? {}) as Record<string, unknown>;
  for (const [k, v] of Object.entries(meta)) {
    if (!RESERVED_META_KEYS.has(k)) out[k] = v;
  }
  return out;
};

// Custom meta -> editor rows. Non-string values are shown as JSON so they can be
// edited losslessly and re-parsed on save.
const metaToEntries = (asset?: RestaurantAsset | null): MetaEntry[] =>
  Object.entries(extractCustomMeta(asset)).map(([key, v]) => ({
    key,
    value: typeof v === "string" ? v : JSON.stringify(v),
  }));

// Editor rows -> custom meta. Blank keys are skipped; values that parse as JSON
// (numbers, booleans, objects) keep their type, otherwise stay plain strings.
const entriesToMeta = (entries: MetaEntry[]): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const { key, value } of entries) {
    const k = key.trim();
    if (!k) continue;
    const trimmed = value.trim();
    let parsed: unknown = value;
    if (trimmed) {
      try {
        parsed = JSON.parse(trimmed);
      } catch {
        parsed = value;
      }
    }
    out[k] = parsed;
  }
  return out;
};

// Schema for the custom-metadata editor: a list of {key, value} string pairs.
const CustomMetaSchema = Type.Object({
  entries: Type.Array(
    Type.Object({ key: Type.String(), value: Type.String() }),
  ),
});

export const AssetDetails = defineComponent<AssetDetailsProps>({
  name: "AssetDetails",
  props: [
    "mode",
    "asset",
    "assetType",
    "isOpen",
    "onClose",
    "onSave",
    "typeOptions",
    "uploadsApi",
  ],
  setup(props) {
    const { t } = useLocalization();
    // Owner-scoped API; Shop is the default so existing callers are unchanged.
    const uploads = props.uploadsApi ?? uploadsAPI;
    const uploading = ref(false);
    const selectedFile = ref<File | null>(null);
    const previewUrl = ref<string | null>(null);
    const error = ref<string | null>(null);
    const isDragging = ref(false);
    const existingAssets = ref<RestaurantAsset[]>([]);
    const loadingAssets = ref(false);
    const selectedExistingAsset = ref<RestaurantAsset | null>(null);
    const selectedType = ref<string>(props.assetType);
    const newCollectionName = ref("");

    // ── View-mode editing state ──────────────────────────────────────────────
    const editType = ref<string>("");
    const newEditCollectionName = ref("");
    const metaForm = ref<{ entries: MetaEntry[] }>({ entries: [] });
    const saving = ref(false);
    const saveError = ref<string | null>(null);
    const replacing = ref(false);
    const replaceError = ref<string | null>(null);

    const resetEditState = (asset?: RestaurantAsset | null) => {
      editType.value = asset?.type ?? "";
      newEditCollectionName.value = "";
      metaForm.value = { entries: metaToEntries(asset) };
      saveError.value = null;
      replaceError.value = null;
    };

    watch(
      [() => props.isOpen, () => props.asset],
      ([isOpen]) => {
        if (isOpen && props.mode === "view") resetEditState(props.asset);
      },
      { immediate: true },
    );

    // Compact, label-less key/value row for each metadata entry.
    const metaItemTemplate = (locals: StructLocals) => (
      <div class="flex gap-2">
        <div class="w-2/5">{locals.inputs.key?.()}</div>
        <div class="flex-1">{locals.inputs.value?.()}</div>
      </div>
    );

    // List chrome tuned for a key/value editor: inline delete, no reordering.
    const metaListTemplate = listTemplate.clone({
      renderItem: (locals, item, index) => (
        <div key={item.key} class="flex items-center gap-2">
          <div class="flex-1">{item.input()}</div>
          <button
            type="button"
            class="btn btn-ghost btn-xs btn-circle text-base-content/40 hover:text-error"
            onClick={() => locals.onRemove(index)}
            title={locals.i18n.remove}
          >
            <span class="material-symbols-outlined text-sm">close</span>
          </button>
        </div>
      ),
      renderAddButton: (locals) => (
        <button
          type="button"
          class="btn btn-ghost btn-xs mt-1 gap-1 self-start text-primary"
          onClick={locals.onAdd}
        >
          <span class="material-symbols-outlined text-sm">add</span>
          {locals.i18n.add}
        </button>
      ),
    });

    const metaFormOptions = computed<FormOptions>(() => ({
      auto: "none",
      fields: {
        entries: {
          label: false,
          disableOrder: true,
          i18n: { add: t("settings.assets.add_meta_field") },
          template: metaListTemplate,
          item: {
            template: metaItemTemplate,
            fields: {
              key: {
                label: false,
                attrs: { placeholder: t("settings.assets.meta_key") },
              },
              value: {
                label: false,
                attrs: { placeholder: t("settings.assets.meta_value") },
              },
            },
          },
        },
      },
    }));

    const handleSaveEdits = async () => {
      if (!props.asset) return;
      const nextType =
        editType.value === NEW_COLLECTION_VALUE
          ? assetCollectionTypeFromName(newEditCollectionName.value)
          : editType.value;
      if (!nextType) {
        saveError.value = t("settings.assets.collection_name_required");
        return;
      }
      try {
        saving.value = true;
        saveError.value = null;
        const meta = entriesToMeta(metaForm.value.entries);
        const updated = await uploads.updateAsset(props.asset.id, {
          meta,
          type: nextType,
        });
        props.onSave?.(updated);
      } catch (err) {
        console.error("Failed to save asset changes:", err);
        saveError.value = t("settings.assets.update_error");
      } finally {
        saving.value = false;
      }
    };

    const handleReplaceFile = async (e: Event) => {
      const input = e.target as HTMLInputElement;
      const file = input.files?.[0];
      input.value = "";
      if (!file || !props.asset) return;
      if (
        file.type.startsWith("image/") &&
        file.size > MAX_IMAGE_UPLOAD_BYTES
      ) {
        replaceError.value = t("settings.assets.image_too_large");
        return;
      }
      try {
        replacing.value = true;
        replaceError.value = null;
        await uploads.replaceAsset(props.asset.id, file);
        props.onSave?.(props.asset);
      } catch (err) {
        console.error("Failed to replace asset:", err);
        replaceError.value =
          err instanceof Error
            ? err.message
            : t("settings.assets.replace_error");
      } finally {
        replacing.value = false;
      }
    };

    const isImage = computed(() => {
      const contentType = (props.asset?.meta?.content_type as string) || "";
      return contentType.startsWith("image/");
    });

    const isVideo = computed(() => {
      const contentType = (props.asset?.meta?.content_type as string) || "";
      return contentType.startsWith("video/");
    });

    const dimensions = computed(() => {
      const w = props.asset?.meta?.width;
      const h = props.asset?.meta?.height;
      return typeof w === "number" && typeof h === "number"
        ? `${w} × ${h}`
        : null;
    });

    // Manual ordering (meta.position, set by drag-reorder on the grid). Shown
    // read-only, 1-based, since it is owned by the reorder endpoint.
    const position = computed(() => {
      const raw = props.asset?.meta?.position;
      return typeof raw === "number" ? raw + 1 : null;
    });

    const copied = ref(false);
    const copyUrl = async () => {
      if (!props.asset) return;
      try {
        await navigator.clipboard.writeText(props.asset.url);
        copied.value = true;
        setTimeout(() => (copied.value = false), 1500);
      } catch (err) {
        console.error("Failed to copy URL:", err);
      }
    };

    const loadExistingAssets = async () => {
      if (props.mode !== "add") return;

      if (selectedType.value === NEW_COLLECTION_VALUE) {
        existingAssets.value = [];
        return;
      }

      try {
        loadingAssets.value = true;
        const assets = await uploads.getAssets({ type: selectedType.value });
        existingAssets.value = assets;
      } catch (err) {
        console.error("Failed to load existing assets:", err);
      } finally {
        loadingAssets.value = false;
      }
    };

    // Immediate: callers usually mount this already open, and the library
    // must load then too, not only when isOpen later flips.
    watch(
      () => props.isOpen,
      (isOpen) => {
        if (isOpen && props.mode === "add") {
          selectedType.value = props.assetType;
          newCollectionName.value = "";
          loadExistingAssets();
        }
      },
      { immediate: true },
    );

    watch(selectedType, () => {
      if (props.isOpen && props.mode === "add") {
        loadExistingAssets();
      }
    });

    const handleFileSelect = (e: Event) => {
      const input = e.target as HTMLInputElement;
      const file = input.files?.[0];
      if (file) {
        processFile(file);
      }
    };

    const processFile = (file: File) => {
      if (
        file.type.startsWith("image/") &&
        file.size > MAX_IMAGE_UPLOAD_BYTES
      ) {
        selectedFile.value = null;
        previewUrl.value = null;
        error.value = t("settings.assets.image_too_large");
        return;
      }
      selectedFile.value = file;
      selectedExistingAsset.value = null;
      error.value = null;

      if (file.type.startsWith("image/") || file.type.startsWith("video/")) {
        previewUrl.value = URL.createObjectURL(file);
      }
    };

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      isDragging.value = true;
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      isDragging.value = false;
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      isDragging.value = false;

      const file = e.dataTransfer?.files[0];
      if (file) {
        processFile(file);
      }
    };

    const handleExistingAssetSelect = (asset: RestaurantAsset) => {
      selectedExistingAsset.value = asset;
      selectedFile.value = null;
      previewUrl.value = null;
      error.value = null;

      if (props.onSave) {
        props.onSave(asset);
      }
    };

    const handleUpload = async () => {
      if (!selectedFile.value) return;
      const uploadType =
        selectedType.value === NEW_COLLECTION_VALUE
          ? assetCollectionTypeFromName(newCollectionName.value)
          : selectedType.value;
      if (!uploadType) {
        error.value = t("settings.assets.collection_name_required");
        return;
      }

      try {
        uploading.value = true;
        error.value = null;

        const result = await uploads.uploadAsset(
          selectedFile.value,
          uploadType,
        );

        const assets = await uploads.getAssets({ type: uploadType });
        const newAsset = assets.find((a) => a.id === result.assetId);

        if (newAsset && props.onSave) {
          props.onSave(newAsset);
        }

        selectedFile.value = null;
        previewUrl.value = null;
        props.onClose();
      } catch (err) {
        console.error("Upload failed:", err);
        error.value =
          err instanceof Error
            ? err.message
            : t("settings.assets.upload_error");
      } finally {
        uploading.value = false;
      }
    };

    const formatDate = (dateStr: string) => {
      return new Date(dateStr).toLocaleString();
    };

    const formatFileSize = (bytes: number) => {
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const title = computed(() =>
      props.mode === "add"
        ? t("settings.assets.upload_title")
        : t("settings.assets.details_title"),
    );

    // ── View-mode subviews ─────────────────────────────────────────────────
    const renderPreview = () => (
      <div class="flex items-center justify-center overflow-hidden rounded-lg border  bg-base-200 p-1.5">
        {isImage.value ? (
          <img
            src={props.asset!.url}
            alt="Asset"
            class="max-h-48 w-full rounded object-contain"
          />
        ) : isVideo.value ? (
          <video
            src={props.asset!.url}
            controls
            class="max-h-48 w-full rounded"
          />
        ) : (
          <div class="flex h-28 w-full items-center justify-center">
            <span class="material-symbols-outlined text-5xl text-base-content/30">
              insert_drive_file
            </span>
          </div>
        )}
      </div>
    );

    const renderDetailRow = (label: string, value: VNode | string) => (
      <div class="flex items-center justify-between gap-4 py-1.5">
        <span class="shrink-0 text-[11px] font-medium uppercase tracking-wide text-base-content/45">
          {label}
        </span>
        <span class="min-w-0 truncate text-right text-xs text-base-content">
          {value}
        </span>
      </div>
    );

    // Compact facts strip (system-owned, read-only). Collection is edited below
    // so it is not repeated here.
    const renderDetails = () => (
      <div class="divide-y divide-base-200 rounded-lg border  bg-base-100 px-3">
        {renderDetailRow(
          t("settings.assets.content_type"),
          (props.asset!.meta?.content_type as string) ||
            t("settings.assets.unknown"),
        )}
        {dimensions.value
          ? renderDetailRow(t("settings.assets.dimensions"), dimensions.value)
          : null}
        {position.value != null
          ? renderDetailRow(t("settings.assets.position"), `#${position.value}`)
          : null}
        {renderDetailRow(
          t("settings.assets.created"),
          formatDate(props.asset!.created_at),
        )}
        {renderDetailRow(
          t("settings.assets.url"),
          <button
            type="button"
            class="inline-flex items-center gap-1 font-mono text-xs text-primary hover:underline"
            onClick={copyUrl}
            title={props.asset!.url}
          >
            <span class="material-symbols-outlined text-sm">
              {copied.value ? "check" : "content_copy"}
            </span>
            {copied.value
              ? t("settings.assets.copied")
              : t("settings.assets.copy_url")}
          </button>,
        )}
      </div>
    );

    const renderEditCard = () => (
      <div class="space-y-3 rounded-lg border  bg-base-100 p-3">
        <h3 class="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-base-content/70">
          <span class="material-symbols-outlined text-sm text-primary">
            tune
          </span>
          {t("settings.assets.edit_metadata")}
        </h3>

        {props.typeOptions && props.typeOptions.length > 0 && (
          <div class="space-y-2">
            <select
              class="select select-sm select-bordered w-full"
              value={editType.value}
              onChange={(e) =>
                (editType.value = (e.target as HTMLSelectElement).value)
              }
            >
              {props.typeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
              <option value={NEW_COLLECTION_VALUE}>
                {t("settings.assets.create_collection")}
              </option>
            </select>
            {editType.value === NEW_COLLECTION_VALUE && (
              <input
                type="text"
                class="input input-sm input-bordered w-full"
                value={newEditCollectionName.value}
                placeholder={t("settings.assets.collection_name_placeholder")}
                onInput={(e) =>
                  (newEditCollectionName.value = (
                    e.target as HTMLInputElement
                  ).value)
                }
              />
            )}
          </div>
        )}

        <div class="space-y-1">
          <SchemaForm
            schema={CustomMetaSchema}
            options={metaFormOptions.value}
            value={metaForm.value}
            onChange={(v: { entries: MetaEntry[] }) => (metaForm.value = v)}
          />
        </div>

        {saveError.value && (
          <div class="alert alert-error py-2">
            <span class="material-symbols-outlined text-sm">error</span>
            <span class="text-sm">{saveError.value}</span>
          </div>
        )}

        <button
          type="button"
          class="btn btn-primary btn-sm w-full"
          disabled={saving.value}
          onClick={handleSaveEdits}
        >
          {saving.value && (
            <span class="loading loading-spinner loading-sm"></span>
          )}
          {saving.value
            ? t("settings.assets.saving")
            : t("settings.assets.save_button")}
        </button>
      </div>
    );

    const renderReplaceCard = () => (
      <div class="rounded-lg border  bg-base-100 p-3">
        <div class="flex items-center justify-between gap-3">
          <div class="min-w-0">
            <h3 class="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-base-content/70">
              <span class="material-symbols-outlined text-sm text-primary">
                swap_horiz
              </span>
              {t("settings.assets.replace_file")}
            </h3>
            <p class="mt-0.5 text-[11px] text-base-content/45">
              {t("settings.assets.replace_hint")}
            </p>
          </div>
          <label class="btn btn-outline btn-sm shrink-0">
            {replacing.value ? (
              <span class="loading loading-spinner loading-sm"></span>
            ) : (
              <span class="material-symbols-outlined text-sm">upload</span>
            )}
            {replacing.value
              ? t("settings.assets.replacing")
              : t("settings.assets.choose_replacement")}
            <input
              type="file"
              class="hidden"
              accept="image/*,video/*"
              disabled={replacing.value}
              onChange={handleReplaceFile}
            />
          </label>
        </div>
        {replaceError.value && (
          <div class="alert alert-error mt-2 py-2">
            <span class="material-symbols-outlined text-sm">error</span>
            <span class="text-sm">{replaceError.value}</span>
          </div>
        )}
      </div>
    );

    return () => (
      <RightSheet
        title={title.value}
        isOpen={props.isOpen}
        onClose={props.onClose}
        width="600px"
      >
        <div class="space-y-6 p-4">
          {props.mode === "add" ? (
            /* Upload Mode */
            <div class="space-y-6">
              {/* Collection / type selector */}
              {props.typeOptions && props.typeOptions.length > 0 && (
                <div class="space-y-2">
                  <label class="label">
                    <span class="label-text font-medium text-accent">
                      {t("settings.assets.collection_label")}
                    </span>
                  </label>
                  <select
                    class="select select-bordered w-full"
                    value={selectedType.value}
                    onChange={(e) =>
                      (selectedType.value = (
                        e.target as HTMLSelectElement
                      ).value)
                    }
                  >
                    {props.typeOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                    <option value={NEW_COLLECTION_VALUE}>
                      {t("settings.assets.create_collection")}
                    </option>
                  </select>
                  {selectedType.value === NEW_COLLECTION_VALUE && (
                    <div class="space-y-1">
                      <input
                        type="text"
                        class="input input-bordered w-full"
                        value={newCollectionName.value}
                        placeholder={t(
                          "settings.assets.collection_name_placeholder",
                        )}
                        onInput={(e) =>
                          (newCollectionName.value = (
                            e.target as HTMLInputElement
                          ).value)
                        }
                      />
                      <p class="text-xs text-base-content/50">
                        {t("settings.assets.collection_created_on_upload")}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Dropzone */}
              <div class="space-y-3">
                <label class="label">
                  <span class="label-text font-medium text-accent">
                    {t("settings.assets.upload_new")}
                  </span>
                </label>
                <div
                  class={`
                    relative cursor-pointer rounded-xl border-2 border-dashed p-8 transition-all duration-200
                    ${
                      isDragging.value
                        ? "scale-[1.02] border-primary bg-primary/10"
                        : " hover:border-primary/50 hover:bg-base-200/50"
                    }
                  `}
                  onDragover={handleDragOver}
                  onDragleave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => {
                    const input = document.getElementById(
                      "file-input",
                    ) as HTMLInputElement;
                    input?.click();
                  }}
                >
                  <input
                    id="file-input"
                    type="file"
                    class="hidden"
                    accept="image/*,video/*"
                    onChange={handleFileSelect}
                  />

                  {!previewUrl.value ? (
                    <div class="space-y-3 text-center">
                      <div
                        class={`mx-auto flex h-16 w-16 items-center justify-center rounded-full transition-colors ${
                          isDragging.value ? "bg-primary/20" : "bg-base-300"
                        }`}
                      >
                        <span
                          class={`material-symbols-outlined text-4xl transition-colors ${
                            isDragging.value
                              ? "text-primary"
                              : "text-base-content/50"
                          }`}
                        >
                          upload
                        </span>
                      </div>
                      <div>
                        <p class="mb-1 text-base font-medium text-accent">
                          {isDragging.value
                            ? t("settings.assets.drop_here")
                            : t("settings.assets.drop_or_browse")}
                        </p>
                        <p class="text-sm text-base-content/60">
                          {t("settings.assets.supports_media")}
                        </p>
                      </div>
                    </div>
                  ) : (
                    /* Preview */
                    <div class="animate-in fade-in zoom-in space-y-4 duration-300">
                      <div class="relative overflow-hidden rounded-lg bg-base-300 shadow-lg">
                        {selectedFile.value?.type.startsWith("image/") ? (
                          <img
                            src={previewUrl.value}
                            alt="Preview"
                            class="max-h-80 w-full object-contain"
                          />
                        ) : selectedFile.value?.type.startsWith("video/") ? (
                          <video
                            src={previewUrl.value}
                            controls
                            class="max-h-80 w-full"
                          />
                        ) : null}

                        <button
                          class="btn btn-circle btn-sm btn-error absolute right-2 top-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            selectedFile.value = null;
                            previewUrl.value = null;
                          }}
                        >
                          <span class="material-symbols-outlined text-sm">
                            close
                          </span>
                        </button>
                      </div>

                      {/* File Details */}
                      <div class="space-y-2 rounded-lg bg-base-200 p-4">
                        <div class="flex items-center gap-2">
                          <span class="material-symbols-outlined text-xl text-primary">
                            {selectedFile.value?.type.startsWith("image/")
                              ? "image"
                              : "videocam"}
                          </span>
                          <p class="flex-1 truncate text-sm font-medium text-accent">
                            {selectedFile.value?.name}
                          </p>
                        </div>
                        <div class="flex gap-4 text-xs text-base-content/60">
                          <span>
                            {t("settings.assets.file_type")}:{" "}
                            {selectedFile.value?.type}
                          </span>
                          <span>
                            {t("settings.assets.file_size")}:{" "}
                            {selectedFile.value &&
                              formatFileSize(selectedFile.value.size)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Error Message */}
              {error.value && (
                <div class="alert alert-error animate-in slide-in-from-top duration-200">
                  <span class="material-symbols-outlined">error</span>
                  <span>{error.value}</span>
                </div>
              )}

              {/* Upload Button */}
              {selectedFile.value && (
                <div class="animate-in slide-in-from-bottom duration-300">
                  <button
                    type="button"
                    class="btn btn-primary w-full"
                    disabled={uploading.value}
                    onClick={handleUpload}
                  >
                    {uploading.value && (
                      <span class="loading loading-spinner loading-sm"></span>
                    )}
                    {uploading.value
                      ? t("settings.assets.uploading")
                      : t("settings.assets.upload_button")}
                  </button>
                </div>
              )}

              {/* Divider */}
              <div class="divider text-base-content/40">
                {t("settings.assets.or_select_existing")}
              </div>

              {/* Existing Assets */}
              <div class="space-y-3">
                <label class="label">
                  <span class="label-text font-medium text-accent">
                    {t("settings.assets.select_from_library")}
                  </span>
                  <span class="label-text-alt text-base-content/60">
                    {t("settings.assets.assets_count", [
                      existingAssets.value.length,
                    ])}
                  </span>
                </label>

                {loadingAssets.value ? (
                  <div class="flex items-center justify-center py-8">
                    <span class="loading loading-spinner loading-lg text-primary"></span>
                  </div>
                ) : existingAssets.value.length === 0 ? (
                  <div class="rounded-lg bg-base-200 py-8 text-center">
                    <span class="material-symbols-outlined mb-2 text-4xl text-base-content/30">
                      photo_library
                    </span>
                    <p class="text-sm text-base-content/60">
                      {t("settings.assets.no_library_assets")}
                    </p>
                  </div>
                ) : (
                  <div class="grid max-h-96 grid-cols-3 gap-3 overflow-y-auto p-1">
                    {existingAssets.value.map((asset) => (
                      <div
                        key={asset.id}
                        class={`
                          relative aspect-square cursor-pointer overflow-hidden rounded-lg
                          transition-all duration-200 hover:scale-105 hover:shadow-lg
                          ${
                            selectedExistingAsset.value?.id === asset.id
                              ? "scale-105 shadow-lg ring-2 ring-primary"
                              : "hover:ring-2 hover:ring-primary/50"
                          }
                        `}
                        onClick={() => handleExistingAssetSelect(asset)}
                      >
                        {(asset.meta?.content_type as string)?.startsWith(
                          "image/",
                        ) ? (
                          <img
                            src={asset.url}
                            alt="Asset"
                            class="h-full w-full object-cover"
                          />
                        ) : (asset.meta?.content_type as string)?.startsWith(
                            "video/",
                          ) ? (
                          <div class="flex h-full w-full items-center justify-center bg-base-300">
                            <span class="material-symbols-outlined text-3xl text-base-content/50">
                              play_circle
                            </span>
                          </div>
                        ) : (
                          <div class="flex h-full w-full items-center justify-center bg-base-300">
                            <span class="material-symbols-outlined text-3xl text-base-content/50">
                              insert_drive_file
                            </span>
                          </div>
                        )}

                        {selectedExistingAsset.value?.id === asset.id && (
                          <div class="absolute inset-0 flex items-center justify-center bg-primary/20">
                            <div class="flex h-8 w-8 items-center justify-center rounded-full bg-primary">
                              <span class="material-symbols-outlined text-xl text-accent">
                                check
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* View Mode */
            props.asset && (
              <div class="space-y-3">
                {renderPreview()}
                {renderDetails()}
                {renderEditCard()}
                {renderReplaceCard()}
              </div>
            )
          )}
        </div>
      </RightSheet>
    );
  },
});
